import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import localeEnGB from '@angular/common/locales/en-GB';
import {
  provideRouter,
  Router,
  RouteReuseStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { provideState, provideStore } from '@ngrx/store';
import {
  authHttpInterceptorFn,
  AuthService,
  provideAuth0,
} from '@auth0/auth0-angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MessageServiceProvider } from '@letly/ui-common';
import { environment } from '../env/environment';
import { provideEffects } from '@ngrx/effects';
import {
  filtersFeature,
  propertyListFeature,
} from './list-view/data/property-list.reducers';
import { PropertyListEffect } from './list-view/data/property-list.effect';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { createPropertyFeature } from './landlord/create-property/data/create-property.reducers';
import { AmenitiesEffects, amenitiesFeature } from '@letly/state';
import { landlordListingFeature } from './landlord/landlord-listing/data/landlord-listing.reducers';
import { LandlordListingEffect } from './landlord/landlord-listing/data/landlord-listing.effect';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { TranslateModule } from '@ngx-translate/core';
import { APP_CONFIG } from '@letly/config';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { CustomRouteReuseStrategy } from './core/services/custom-route-strategy';
import { attributionAuthorizationParams } from './core/services/utm-attribution.service';
import { provideServiceWorker } from '@angular/service-worker';
import { firstValueFrom, race, timer } from 'rxjs';
import { NATIVE_AUTH_CALLBACK_URL, provideChatLinkRenderer, SplashScreen, UserPreferencesService,
  appointmentStatusVersionInterceptor,
} from '@letly/services';
import { filter } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
} from '@angular/material/dialog';
import { PosthogService } from './core/services/posthog.service';
import { Capacitor } from '@capacitor/core';

registerLocaleData(localeEnGB, 'en-GB');

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: DEFAULT_CURRENCY_CODE,
      useFactory: () => inject(UserPreferencesService).currencyCode(),
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'mediumDate' },
    },
    provideHttpClient(withInterceptors([
      appointmentStatusVersionInterceptor,
      authHttpInterceptorFn,
    ])),
    provideBrowserGlobalErrorListeners(),
    importProvidersFrom(TranslateModule.forRoot()),
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        audience: environment.auth0.audience,
        redirect_uri: Capacitor.isNativePlatform()
          ? NATIVE_AUTH_CALLBACK_URL
          : window.location.origin,
        scope: 'openid profile email offline_access read:current_user',
        ...attributionAuthorizationParams(),
      },
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      cookieDomain: window.location.host,
      httpInterceptor: {
        allowedList: [
          {
            uri: 'http://localhost:3000/*',
            allowAnonymous: false,
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0.audience,
                scope: 'openid profile email offline_access read:current_user',
              },
            },
          },
          {
            uri: `${environment.auth0.audience}*`,
            allowAnonymous: true,
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0.audience,
                scope: 'openid profile email offline_access read:current_user',
              },
            },
          },
          {
            uri: '/api/*',
            allowAnonymous: true,
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0.audience,
                scope: 'openid profile email offline_access read:current_user',
              },
            },
          },
        ],
      },
    }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideRouterStore(),
    provideStore({
      createProperty: createPropertyFeature.reducer,
      router: routerReducer,
    }),
    provideState(propertyListFeature),
    provideState(filtersFeature),
    provideEffects(PropertyListEffect),
    provideState(amenitiesFeature),
    provideEffects(AmenitiesEffects),
    provideState(landlordListingFeature),
    provideEffects(LandlordListingEffect),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideNgxSkeletonLoader({
      theme: { extendsFromRoot: true },
    }),
    MessageServiceProvider,
    provideChatLinkRenderer(),
    {
      provide: APP_CONFIG,
      useValue: {
        spriteUrl: environment.spriteUrl,
        chatIconUrl: environment.chatIconUrl,
        appTitle: environment.appTitle,
        fileStoreBaseUrl: environment.fileStoreBaseUrl,
        listingBaseUrl: environment.listingBaseUrl,
        agenticCommunicationBaseUrl: environment.agenticCommunicationBaseUrl,
        osPlacesApiKey: environment.osPlacesApiKey,
        googleMapsApiKey: environment.googleMapsApiKey,
        listingIntelligenceBaseUrl: environment.listingIntelligenceBaseUrl,
        idealPostcodesApiKey: environment.idealPostcodesApiKey,
        notificationBaseUrl: environment.notificationBaseUrl,
        firebase: environment.firebase,
      },
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: { disabled: true },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: false,
      } satisfies MatDialogConfig,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      const splash = inject(SplashScreen);
      const auth = inject(AuthService);
      firstValueFrom(
        race(auth.isLoading$.pipe(filter((loading) => !loading)), timer(5_000)),
      ).then(() => {
        splash.hide();
      });
    }),
    {
      provide: ErrorHandler,
      useFactory: () => {
        const sentryHandler = Sentry.createErrorHandler();
        const posthog = inject(PosthogService);
        return {
          handleError(error: unknown) {
            sentryHandler.handleError(error);
            posthog.posthog.captureException(error);
          },
        };
      },
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ],
};
